import { gql, GraphQLClient } from 'graphql-request';

import { getAssetFromAddress } from '~config/protocol';
import { NetworkIds } from '~config/rpcConfig';
import { EVestaAsset } from '~config/types';

interface TvlStats {
  id: string;
  currentAssetSystemState: {
    price: string;
    totalCollateral: string;
  };
}

interface VstMinted {
  global: {
    totalVSTDebt: string;
  };
  assetStats: {
    id: string;
    currentAssetSystemState: {
      totalDebt: string;
    };
  }[];
}

export default class Assets {
  constructor(private readonly client: GraphQLClient, private readonly networkId: NetworkIds) {}
  async getTvl(block: number) {
    const tvlQuery = gql`
      query get_tvl($block: Int) {
        assetStats(first: 1000, block: { number: $block }) {
          id
          currentAssetSystemState {
            price
            totalCollateral
          }
        }
      }
    `;

    const { assetStats }: { assetStats: TvlStats[] } = await this.client.request(tvlQuery, { block });

    const collaterals = assetStats.reduce(
      (acc, { id, currentAssetSystemState: { price, totalCollateral } }) => ({
        ...acc,
        [getAssetFromAddress(id, this.networkId)]: {
          collateralLocked: totalCollateral,
          collateralLockedUSD: (Number(totalCollateral) * Number(price)).toString,
        },
      }),
      {} as Record<
        EVestaAsset,
        {
          collateralLocked: string;
          collateralLockedUSD: string;
        }
      >,
    );

    const tvlUsd = Object.values(collaterals)
      .reduce((acc, curr) => {
        return acc + Number(curr.collateralLockedUSD);
      }, 0)
      .toString();

    return {
      collaterals,
      tvlUsd,
    };
  }

  async getVSTMinted(block: number) {
    const vstMintedQuery = gql`
      query get_vst_minted($block: Int) {
        global(id: "only", block: { number: $block }) {
          totalVSTDebt
        }
        assetStats(first: 1000, block: { number: $block }) {
          id
          currentAssetSystemState {
            totalDebt
          }
        }
      }
    `;

    const {
      global: { totalVSTDebt },
      assetStats,
    }: VstMinted = await this.client.request(vstMintedQuery, { block });

    const collaterals = assetStats.reduce(
      (acc, { id, currentAssetSystemState: { totalDebt } }) => ({
        ...acc,
        [getAssetFromAddress(id, this.networkId)]: {
          vstMinted: totalDebt,
        },
      }),
      {} as Record<
        EVestaAsset,
        {
          collateralLocked: string;
          collateralLockedUSD: string;
        }
      >,
    );

    return {
      collaterals,
      totalVSTMinted: totalVSTDebt,
    };
  }
}
